import { useQuery } from '@apollo/client/react';
import { Table, TableContainer, TableRow, TableBody } from '@mui/material';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';

import { AddButton } from 'common/components/Button/AddButton';
import { TableCell } from 'common/components/Table';
import { Divider } from 'common/components/Divider';
import { Chip, ChipGroup } from 'common/components/Chip';
import { LtiPlatformsDocument } from 'graphql/types';
import { Loader } from 'common/components/Loader';
import { getLTIPlatformDateDisplay } from 'connections/utils/date';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';

export const LTIPlatformManager = () => {
    const [translate] = useTranslation();
    const { data, loading } = useQuery(LtiPlatformsDocument);
    const { openGlobalDrawer } = useGlobalDrawer();

    const { ltiPlatforms } = data || {};

    const hasLTIPlatforms = !!ltiPlatforms?.length;

    return (
        <Box>
            {!loading && (
                <AddButton
                    onClick={() => {
                        openGlobalDrawer({
                            type: IGlobalDrawerType.LTICreate,
                        });
                    }}
                >
                    {translate('lTIPlatformForm.lTIPlatform')}
                </AddButton>
            )}
            {loading && (
                <Box sx={{ position: 'relative', height: '200px' }}>
                    <Loader />
                </Box>
            )}
            {hasLTIPlatforms && (
                <>
                    <Box sx={{ mt: 2 }}>
                        <Divider />
                    </Box>

                    <TableContainer>
                        <Table>
                            <TableBody>
                                {ltiPlatforms.map((lTIPlatform) => {
                                    const {
                                        id,
                                        title,
                                        numberOfUsers,
                                        isActive,
                                        deploymentIds,
                                        keySetUrl,
                                        authTokenUrl,
                                        authLoginUrl,
                                        maxUsers,
                                        startDate,
                                        endDate,
                                    } = lTIPlatform;

                                    const actionRequired =
                                        !deploymentIds ||
                                        !keySetUrl ||
                                        !authTokenUrl ||
                                        !authLoginUrl;

                                    const hasChips =
                                        !isActive || actionRequired;

                                    const participantInfo = `${
                                        numberOfUsers || 0
                                    }${
                                        maxUsers > 0 ? `/${maxUsers}` : ''
                                    } ${translate(
                                        'participants'
                                    ).toLowerCase()}`;

                                    const dateDisplay =
                                        getLTIPlatformDateDisplay(
                                            startDate || '',
                                            endDate || ''
                                        );

                                    return (
                                        <TableRow
                                            key={`ltiPlatform-${id}`}
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() =>
                                                openGlobalDrawer({
                                                    type: IGlobalDrawerType.LTIUpdate,
                                                    itemId: id,
                                                })
                                            }
                                        >
                                            <TableCell>
                                                {title}

                                                <Box
                                                    sx={{
                                                        color: 'grey.500',
                                                        mt: '2px',
                                                    }}
                                                >
                                                    {`${participantInfo}${
                                                        dateDisplay
                                                            ? `, ${dateDisplay}`
                                                            : ''
                                                    }`}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {hasChips && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            justifyContent:
                                                                'right',
                                                        }}
                                                    >
                                                        <Box sx={{ mr: 2 }}>
                                                            <ChipGroup>
                                                                {!isActive && (
                                                                    <Chip
                                                                        color="error"
                                                                        label={translate(
                                                                            'inactive'
                                                                        )}
                                                                    />
                                                                )}

                                                                {actionRequired && (
                                                                    <Chip
                                                                        color="error"
                                                                        label={translate(
                                                                            'actionRequired'
                                                                        )}
                                                                    />
                                                                )}
                                                            </ChipGroup>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
};
